import * as Sentry from '@sentry/react';

import { SENTRY_DSN, environmentNames } from 'config/constants';
import { excludeEventMatcherList } from 'constants/sentryErrors';
import getEnvironment from 'helpers/getEnvironment';

/**
 * This function calculates if an event should be to send to sentry
 * or if we want to exclude it.
 *
 * @param {Object} event Sentry event
 * @param {Object} eventMatcher Matcher settings
 * @param {string} [eventMatcher.type] The event type. For example: TypeError
 * @param {string} [eventMatcher.value] The event message. For example: Cannot read properties of undefined
 * @param {string} [eventMatcher.message] The event message in case is not an exception. For example: Cannot read properties of undefined
 * @param {string} [eventMatcher.file] The source file path or string. For example: http://bentobox.com/file.js or just file.js
 *
 * @returns boolean
 */
export function excludeEvent(event, eventMatcher) {
  // Usage restrictions
  const matcherProperties = Object.getOwnPropertyNames(eventMatcher);
  if (
    matcherProperties.includes('file') &&
    matcherProperties.includes('missingStackTrace')
  ) {
    throw Error(
      'This two properties can not be set at the same time, please only use one: "file", "missingStackTrace"'
    );
  }

  try {
    if (event.exception) {
      const matchesArray = [];

      const sentryExcept =
        event.exception.values[event.exception.values.length - 1];

      if (eventMatcher.type && eventMatcher.value) {
        matchesArray.push(sentryExcept.type.includes(eventMatcher.type));
        matchesArray.push(sentryExcept.value.includes(eventMatcher.value));
      }

      if (eventMatcher.missingStackTrace && sentryExcept.stacktrace) {
        matchesArray.push(false);
      }

      if (!eventMatcher.missingStackTrace && !sentryExcept.stacktrace) {
        matchesArray.push(false);
      }

      if (eventMatcher.file) {
        let frameIndex = sentryExcept.stacktrace?.frames?.length - 1;
        let frame = sentryExcept.stacktrace?.frames[frameIndex];

        while ((frame?.filename ?? '') === '[native code]' && frameIndex >= 0) {
          frame = sentryExcept.stacktrace?.frames[frameIndex];
          frameIndex -= 1;
        }

        if (frame) {
          const filenameMatches = (frame.filename ?? '').includes(
            eventMatcher.file
          );
          const absPathMatches = (frame.abs_path ?? '').includes(
            eventMatcher.file
          );
          matchesArray.push(filenameMatches || absPathMatches);
        }
      }

      if (eventMatcher.url) {
        const { url = '' } = event.breadcrumbs?.[0]?.data || {};

        matchesArray.push(url.includes(eventMatcher.url));
      }

      if (eventMatcher.status_codes) {
        const { status_code = 999 } = event.breadcrumbs?.[0]?.data || {};

        matchesArray.push(eventMatcher.status_codes.includes(status_code));
      }

      if (eventMatcher.user) {
        const { email = '' } = event?.user || {};

        matchesArray.push(email.includes(eventMatcher.user));
      }

      if (matchesArray.length > 0) {
        return matchesArray.reduce((acc, value) => acc && value);
      }
    }

    if (eventMatcher.message && event.message) {
      return event.message.includes(eventMatcher.message); // Match found for message
    }
  } catch (error) {
    return false; // Error occurred, return false
  }

  return false; // No match found, return false
}

export const _beforeSend = event => {
  const ignoreEvent = excludeEventMatcherList.some(matcher =>
    excludeEvent(event, matcher)
  );
  if (ignoreEvent) {
    return null;
  }
  event.request = {
    url: window.location.href,
    headers: {
      'User-Agent': window.navigator.userAgent
    }
  };
  return event;
};

export const initializeSentry = () => {
  const { REACT_APP_VERSION } = process.env;

  const ENVIRONMENT_NAME = getEnvironment();

  Sentry.init({
    // add DEVELOPMENT into the array below if you want sentry logging for local development
    dsn: [environmentNames.PRODUCTION, environmentNames.STAGING].includes(
      ENVIRONMENT_NAME
    )
      ? SENTRY_DSN
      : '',
    debug: false,
    defaultIntegrations: false,
    release: REACT_APP_VERSION,
    environment: ENVIRONMENT_NAME,
    sampleRate: 1.0,
    maxBreadcrumbs: 100,
    normalizeDepth: 3,
    integrations: [Sentry.browserTracingIntegration()],
    beforeSend: _beforeSend,
    ignoreErrors: ['prepend is not a function']
  });
};

export const logExceptionToSentry = (e, message) => {
  if (!Sentry.isInitialized()) {
    initializeSentry();
  }
  Sentry.captureException(e, {
    captureContext: { extra: { message, error: JSON.stringify(e) } }
  });
};

export const logToSentry = (message, level) => {
  if (!Sentry.isInitialized()) {
    initializeSentry();
  }
  Sentry.captureMessage(message, level);
};
